/**
 * Gliederung: „Gehört zu“ für Datensätze, Kacheln, Meldungsregeln, Sichten und Prognosen.
 *
 * Zur Wahl stehen die Einträge, an denen ich schreiben darf (dazu der bisherige Eintrag).
 * Ein Einzelkunde hat nur die oberste Ebene: dann gibt es nichts zu wählen und nichts zu sehen.
 */
import { hasStructure, rootOf, type OrganisationNode, type OrganisationsState } from '../lib/organisations.js';

import { Badge } from './ui/badge.js';
import { Label } from './ui/label.js';
import { shortName } from './WorkspacePath.js';

const SELECT = 'h-9 w-full rounded-md border border-input bg-background px-2 text-sm';

/** Einträge, denen ich etwas zuordnen darf; `current` bleibt immer wählbar. */
export function ownerChoices(orgs: OrganisationsState, current?: string | null): OrganisationNode[] {
  return orgs.items.filter((o) => o.id === current || orgs.my.admin || orgs.my.writable.includes(o.id));
}

/** Vorbelegung beim Anlegen: der Arbeitsbereich, sonst der erste Eintrag, an dem ich schreiben darf. */
export function defaultOwner(orgs: OrganisationsState, workspaceId: string | null | undefined): string {
  const choices = ownerChoices(orgs);
  if (workspaceId && choices.some((o) => o.id === workspaceId)) return workspaceId;
  return choices[0]?.id ?? rootOf(orgs.items)?.id ?? '';
}

/** Auswahlfeld; ohne Gliederung null. */
export function GehoertZuSelect({
  id,
  orgs,
  value,
  current,
  onChange,
}: {
  id: string;
  orgs: OrganisationsState;
  value: string;
  current?: string | null;
  onChange: (id: string) => void;
}): JSX.Element | null {
  if (!hasStructure(orgs.items)) return null;
  return (
    <div className="grid gap-1">
      <Label htmlFor={id}>Gehört zu</Label>
      <select id={id} className={SELECT} value={value} onChange={(e) => onChange(e.target.value)}>
        {ownerChoices(orgs, current).map((o) => (
          <option key={o.id} value={o.id}>
            {'– '.repeat(o.depth - 1)}
            {o.name}
          </option>
        ))}
      </select>
    </div>
  );
}

/** Kurzes Abzeichen „Fuchstal“ (ohne Eintrag: oberste Ebene). */
export function OwnerBadge({ items, id }: { items: OrganisationNode[]; id: string | null | undefined }): JSX.Element | null {
  const node = items.find((o) => o.id === id) ?? rootOf(items);
  if (!node) return null;
  return (
    <Badge variant="outline" className="text-[10px] font-normal" title={`Gehört zu ${node.name}`}>
      {shortName(node)}
    </Badge>
  );
}
